import type { ApiErrorBody } from '@amg/protocol';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const FALLBACK = 'Something went wrong. Check your connection and try again.';

/** JSON fetch against our own origin. Errors carry the server's user-facing message. */
export async function api<T>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
      headers: init.body === undefined ? undefined : { 'content-type': 'application/json' },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      credentials: 'same-origin',
      signal: init.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', "Can't reach AMG Support. Check your internet connection.");
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => null)) as (T & Partial<ApiErrorBody>) | null;
  if (!res.ok) throw new ApiError(res.status, data?.error ?? 'error', data?.message ?? FALLBACK);
  return data as T;
}

export function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : FALLBACK;
}
